import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
    GetAllTranscriptions,
    GetTranscriptionsPaginated,
    CreateTranscription,
    EditTranscription,
    DeleteTranscription,
    GetFullTranscriptionById,
    SplitParagraph,
    GenerateDocFromFrontend,
    exportSpeakerSection,
    rawTranscription,
    rtfDownload
  } from "../../services/transcriptions";

  const initialState = {
    transcriptions: [],
    paginatedTranscriptions: [],
    totalTranscriptions: 0,
    currentPage: 1,
    pageSize: 20,
    fullTranscription: null, // Keep for backward compatibility
    fullTranscriptionsById: {}, // New: Store multiple transcriptions by ID
    // The session list request in flight: answers to older ones are dropped,
    // or a refresh sent before a search could land after it.
    paginatedRequestId: null,
    loading: false,
    error: null
  };
  
const transcriptionSlice = createSlice({
    name: "transcriptions",
    initialState,
    reducers: {
        settranscription: (state, { payload }) => {
            state.transcriptions = payload
        },
        removesingletranscription: (state, { payload }) => {
            state.transcriptions = state.transcriptions.filter(i => i.transcription_id !== payload)
        },
        /**
         * Apply an undo to the loaded transcript immediately, without waiting
         * for a refetch.
         *
         * The full payload is megabytes and the client re-normalises every
         * paragraph and redistributes the word list before anything appears,
         * so undo looked slow even though the writes take milliseconds. This
         * paints the restored rows first; the refetch that follows reconciles
         * anything else (timestamps recomputed server-side, for example).
         *
         * payload: { restore: [rows], recreate: [rows], remove: [ids] }
         */
        applyUndoToTranscript: (state, { payload }) => {
            const apply = (full) => {
                if (!full || !Array.isArray(full.paragraphs)) return;
                const removed = new Set((payload.remove || []).map(Number));
                let list = full.paragraphs.filter((p) => !removed.has(Number(p.id)));

                const patch = new Map(
                    [...(payload.restore || []), ...(payload.recreate || [])]
                        .map((r) => [Number(r.id), r])
                );
                list = list.map((p) =>
                    patch.has(Number(p.id)) ? { ...p, ...patch.get(Number(p.id)) } : p
                );
                const present = new Set(list.map((p) => Number(p.id)));
                patch.forEach((row, id) => {
                    if (!present.has(id)) list.push({ ...row });
                });
                full.paragraphs = list;
            };
            apply(state.fullTranscription);
            const id = state.fullTranscription?.id;
            if (id != null) apply(state.fullTranscriptionsById[id]);
        }
    },
    extraReducers: (builder) => {
        builder
          .addCase(getAllTranscriptionsAsync.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(getAllTranscriptionsAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.transcriptions = action.payload;
          })
          .addCase(getAllTranscriptionsAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
          })

          .addCase(getTranscriptionsPaginatedAsync.pending, (state, action) => {
            state.paginatedRequestId = action.meta.requestId;
            state.loading = true;
            state.error = null;
          })
          .addCase(getTranscriptionsPaginatedAsync.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.paginatedRequestId) return;
            state.paginatedRequestId = null;
            state.loading = false;
            state.paginatedTranscriptions = action.payload.items;
            state.totalTranscriptions = action.payload.total;
            state.currentPage = action.payload.page;
            state.pageSize = action.payload.page_size;
          })
          .addCase(getTranscriptionsPaginatedAsync.rejected, (state, action) => {
            if (action.meta.requestId !== state.paginatedRequestId) return;
            state.paginatedRequestId = null;
            state.loading = false;
            state.error = action.error.message;
          })
      
          // Create
          .addCase(createTranscriptionAsync.fulfilled, (state, action) => {
            state.transcriptions.push(action.payload);
          })
      
          // Edit
          .addCase(editTranscriptionAsync.fulfilled, (state, action) => {
            const index = state.transcriptions.findIndex(t => t.id === action.payload.id);
            if (index !== -1) {
              state.transcriptions[index] = {
                ...state.transcriptions[index],
                ...action.payload.updated
              };
            }
          })

          //FullGet
          .addCase(getFullTranscriptionByIdAsync.pending, (state) => {
            state.loading = true;
            state.error = null;
          })
          .addCase(getFullTranscriptionByIdAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.fullTranscription = action.payload; // Keep for backward compatibility
            // Store in the map by ID
            if (action.payload && action.payload.id) {
              state.fullTranscriptionsById[action.payload.id] = action.payload;
            }
          })
          .addCase(getFullTranscriptionByIdAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
          })
      
          // Delete
          .addCase(deleteTranscriptionAsync.fulfilled, (state, action) => {
            state.transcriptions = state.transcriptions.filter(t => t.id !== action.payload);
          });
      }
})

export const getAllTranscriptionsAsync = createAsyncThunk("transcriptions/getAllTranscriptionsAsync", async () => {
    const response = await GetAllTranscriptions();
    console.log(response)
    return response;
})

export const getTranscriptionsPaginatedAsync = createAsyncThunk(
    "transcriptions/getTranscriptionsPaginatedAsync",
    async ({
      page = 1,
      page_size = 20,
      search = "",
      date_from = "",
      date_to = "",
      stages = [],
      tag_ids = [],
    } = {}) => {
      return await GetTranscriptionsPaginated({
        page,
        page_size,
        search,
        date_from,
        date_to,
        stages,
        tag_ids,
      });
    }
);

export const createTranscriptionAsync = createAsyncThunk(
    "transcriptions/createTranscriptionAsync",
    async (payload) => {
      const response = await CreateTranscription(payload);
      return response;
    }
  );

export const editTranscriptionAsync = createAsyncThunk(
    "transcriptions/editTranscriptionAsync",
    async ({ id, payload }) => {
      const response = await EditTranscription(payload, id);
      return { id, updated: response };
    }
  );

export const deleteTranscriptionAsync = createAsyncThunk(
    "transcriptions/deleteTranscriptionAsync",
    async (id) => {
      await DeleteTranscription(id);
      return id;
    }
  );


  export const getFullTranscriptionByIdAsync = createAsyncThunk(
    "transcriptions/getFullTranscriptionByIdAsync",
    async (id) => {
      const response = await GetFullTranscriptionById(id);
      return response;
    }
  );  

  export const splitParagraphAsync = createAsyncThunk(
    "transcriptions/splitParagraphAsync",
    async (payload) => {
      const response = await SplitParagraph(payload);
      return response;
    }
  );



  export const generateDocFromFrontendAsync = createAsyncThunk(
    "transcriptions/generateDocFromFrontendAsync",
    async (payload) => {
      const response = await GenerateDocFromFrontend(payload);
      return response;
    }
  );
  
  export const exportSpeakerSectionAsync = createAsyncThunk(
    "transcriptions/exportSpeakerSectionAsync",
    async (payload) => {
      const response = await exportSpeakerSection(payload);
      return response;
    }
  );

  export const rawTranscriptionDownloadAsync = createAsyncThunk(
    "transcriptions/rawTranscriptionAsync",
    async (payload) => {
      const response = await rawTranscription(payload);
      return response;
    }
  );

  export const rtfDownloadAsync = createAsyncThunk(
    "transcriptions/rtfDownloadAsync",
    async (id) => {
      const response = await rtfDownload(id);
      return response;
    }
  );
  
  
export const {
  settranscription,
  removesingletranscription,
  applyUndoToTranscript
} = transcriptionSlice.actions;



export default transcriptionSlice.reducer;
